import { CoursePathSegment } from '@/data/hook/course-line/useCoursesSegments';
import Observable from '../observers/ObserverPattern';
import { Tree } from './Tree';

export type CoursesModelsType =
  | 'courseLine'
  | 'module'
  | 'course'
  | 'unit'
  | 'lesson';

export type CoursesNode = {
  id: string;
  realId: number | string;
  name: string;
  type: CoursesModelsType | 'root';
  href?: string;
};

export type TreeCoursesConstructorProps = {
  root: CoursesNode;
  segments?: CoursePathSegment[];
};

export default class TreeCourses extends Tree<string, CoursesNode> {
  observer: Observable<string, CoursesNode[]>;
  segments: CoursePathSegment[];
  currentSegment?: CoursePathSegment;
  constructor({ root, segments }: TreeCoursesConstructorProps) {
    super(root);
    this.observer = new Observable<string, CoursesNode[]>();
    this.segments = segments ?? [];
    this.currentSegment = this.segments.at(-1);
  }

  getCurrentPath = () => this.findPath(this.currentSegment?.key ?? '') ?? [];

  setSegments = (segments: CoursePathSegment[]) => {
    this.segments = segments;
    this.currentSegment = this.segments.at(-1);
    this.observer.notifyAll(undefined, this.getCurrentPath());
  };

  notifyTreeUpdate = (courseLine: CoursesNode) => {
    if (this.currentSegment?.key === courseLine.id)
      this.observer.notifyAll(undefined, this.findPath(courseLine.id) ?? []);
  };

  private replace = (courseLine: CoursesNode, parentElementId?: string) => {
    const node = this.nodes[courseLine.id];
    const parentNode = this.nodes[parentElementId ?? ''];
    if (!parentNode) return;
    this.nodes[node.parentId ?? '']?.children.delete(courseLine.id);
    parentNode.children.set(courseLine.id, node);
    node.parentId = parentNode.data.id;
    node.data = { ...node.data, ...courseLine };
  };

  upsert = (courseLine: CoursesNode, parentElementId?: string) => {
    if (this.nodes[courseLine.id]) this.replace(courseLine, parentElementId);
    else super.insert(courseLine, parentElementId);
    this.notifyTreeUpdate(courseLine);
  };

  update = (courseLine: Partial<CoursesNode> & { id: string }) => {
    super.update(courseLine);
    if (this.segments.some(segment => segment.key === courseLine.id)) {
      this.observer.notifyAll(undefined, this.findPath(courseLine.id) ?? []);
    }
  };
}
